import { defineComponent } from 'vue'

import store from '@/legacy/store'
import { FLAG_SEGMENTS } from '@/lib/constants/flags'
import { hubspotTrackPageView, initializationGuard, publicGuard } from '@/lib/guards'
import { loginRedirect } from '@/lib/guards/loginRedirect.guards'
import { registrationRedirect } from '@/lib/guards/registrationRedirect.guards'
import { PAGE_NAME_MAPPING } from '@/lib/helpers/navigation.helpers'
import { unleashClient } from '@/lib/plugins/unleash'
import { AdvertiserVisibility } from '@/types/advertiser'
import AdminError from '@/views/AdminError.vue'
import AuthenticatedView from '@/views/AuthenticatedView.vue'
import BannedUser from '@/views/BannedUser.vue'
import CampaignCreation from '@/views/CampaignCreation.vue'
import CampaignEditing from '@/views/CampaignEditing.vue'
import CampaignParticipants from '@/views/CampaignParticipants.vue'
import Campaigns from '@/views/CampaignsComponent.vue'
import Channel from '@/views/Channel.vue'
import Channels from '@/views/Channels.vue'
import ContentLibrary from '@/views/ContentLibrary.vue'
import Costs from '@/views/Costs.vue'
import EditAdvertiser from '@/views/EditAdvertiser.vue'
import ErrorPage from '@/views/ErrorPage.vue'
import Influencer from '@/views/Influencer.vue'
import Invoices from '@/views/Invoices.vue'
import LogoutPage from '@/views/LogoutPage.vue'
import MainWrapper from '@/views/MainWrapper.vue'
import NotFound from '@/views/NotFound.vue'
import OnboardingFormPage from '@/views/OnboardingFormPage.vue'
import Orders from '@/views/Orders.vue'
import PaymentPage from '@/views/PaymentPage.vue'
import Request from '@/views/Request.vue'
import SalesPixel from '@/views/Sales/SalesPixel.vue'
import ShopifySetup from '@/views/Sales/ShopifySetup.vue'
import WooCommerce from '@/views/Sales/WooCommerce.vue'
import Segments from '@/views/Segments.vue'

export default [
  {
    path: '/',
    name: 'main-layout',
    component: AuthenticatedView,
    beforeEnter: [initializationGuard],
    children: [
      {
        path: '/',
        name: 'overview',
        component: MainWrapper,
        beforeEnter: [hubspotTrackPageView],
        meta: { title: PAGE_NAME_MAPPING.overview },
      },
      {
        path: '/channel',
        name: 'channel',
        component: Channel,
        beforeEnter: [
          hubspotTrackPageView,
          () => {
            if (store.getters['shop/hideUsers']) {
              return { name: 'Overview' }
            }
          },
        ],
        meta: { title: PAGE_NAME_MAPPING.channel },
      },
      {
        path: '/channels/:tab?',
        name: 'channels',
        component: Channels,
        beforeEnter: [
          hubspotTrackPageView,
          () => {
            if (store.getters['shop/hideUsers']) {
              return { name: 'overview' }
            }
          },
        ],
        meta: { title: PAGE_NAME_MAPPING.channels },
      },
      {
        path: '/invoices',
        name: 'invoices',
        component: Invoices,
        beforeEnter: [hubspotTrackPageView],
        meta: { title: PAGE_NAME_MAPPING.invoices },
      },
      {
        path: '/creators/:id/:tab?',
        name: 'creator',
        component: Influencer,
        props: (route) => ({ id: route.params.id }),
        beforeEnter: [
          hubspotTrackPageView,
          () => {
            if (
              store.getters['shop/hideUsers'] ||
              store.getters['shop/hideSales'] ||
              store.getters['shop/hideCosts']
            ) {
              return false
            }
          },
        ],
        meta: { title: PAGE_NAME_MAPPING.creator },
      },
      {
        path: '/influencers/:id/:tab?',
        redirect: (to) => ({ name: 'creator', params: to.params }),
      },
      {
        path: '/orders',
        name: 'orders',
        component: Orders,
        beforeEnter: [
          hubspotTrackPageView,
          () => {
            if (store.getters['shop/hideSales']) {
              return { name: 'overview' }
            }
          },
        ],
        meta: { title: PAGE_NAME_MAPPING.orders },
      },
      {
        path: '/sales',
        redirect: { name: 'orders' },
      },
      {
        path: '/sales/sales-pixel',
        redirect: { name: 'orders-sales-pixel' },
      },
      {
        path: '/sales/woo-commerce',
        redirect: { name: 'orders-woo-commerce' },
      },
      {
        path: '/sales/shopify',
        redirect: { name: 'orders-shopify' },
      },
      {
        path: '/orders/sales-pixel',
        name: 'orders-sales-pixel',
        component: SalesPixel,
        meta: { title: PAGE_NAME_MAPPING.salesPixel },
      },
      {
        path: '/orders/woo-commerce',
        name: 'orders-woo-commerce',
        component: WooCommerce,
        beforeEnter: [hubspotTrackPageView],
        meta: { title: PAGE_NAME_MAPPING.wooCommerce },
      },
      {
        path: '/orders/shopify',
        name: 'orders-shopify',
        component: ShopifySetup,
        meta: { title: PAGE_NAME_MAPPING.shopify },
      },
      {
        path: '/costs',
        name: 'costs',
        component: Costs,
        beforeEnter: [
          hubspotTrackPageView,
          () => {
            if (store.getters['shop/hideEdit']) {
              return { name: 'overview' }
            }

            if (
              ![AdvertiserVisibility.ALL_USERS, AdvertiserVisibility.ALL_VERIFIED_USERS].includes(
                store.getters['shop/hidden'],
              )
            ) {
              return { name: 'overview' }
            }
          },
        ],
        meta: { title: PAGE_NAME_MAPPING.costs },
      },
      {
        path: '/edit-advertiser',
        name: 'edit-advertiser',
        component: EditAdvertiser,
        beforeEnter: [hubspotTrackPageView],
        meta: { title: PAGE_NAME_MAPPING.editAdvertiser },
      },
      {
        path: '/edit-store',
        redirect: '/edit-advertiser',
      },
      {
        path: '/onboarding',
        name: 'onboarding',
        component: OnboardingFormPage,
        beforeEnter: [hubspotTrackPageView],
        meta: { title: PAGE_NAME_MAPPING.onboarding, layout: 'fullscreen' },
      },
      {
        path: '/payment',
        name: 'payment',
        component: PaymentPage,
        props: (route) => ({
          setupIntentId: route.query.setup_intent,
        }),
        beforeEnter: [hubspotTrackPageView],
        meta: { title: PAGE_NAME_MAPPING.payment, layout: 'fullscreen' },
      },
      {
        path: '/request',
        name: 'request',
        component: Request,
        meta: { title: PAGE_NAME_MAPPING.request, layout: 'fullscreen' },
      },

      {
        path: '/campaigns',
        name: 'campaigns',
        component: Campaigns,
        beforeEnter: [hubspotTrackPageView],
        meta: { title: PAGE_NAME_MAPPING.campaigns },
      },
      {
        path: '/campaigns/create',
        name: 'campaign-creation',
        component: CampaignCreation,
        beforeEnter: [hubspotTrackPageView],
        meta: { title: PAGE_NAME_MAPPING.createCampaign, layout: 'fullscreen' },
      },
      {
        path: '/campaigns/:id',
        name: 'campaign-editing',
        component: CampaignEditing,
        beforeEnter: [hubspotTrackPageView],
        meta: { title: PAGE_NAME_MAPPING.editCampaign, layout: 'fullscreen' },
      },
      {
        path: '/campaign-users/:id',
        name: 'campaign-users',
        component: CampaignParticipants,
        beforeEnter: [hubspotTrackPageView],
        meta: {
          title: PAGE_NAME_MAPPING.campaignParticipants,
          dynamicTitle: true,
          back: { name: 'campaigns' },
        },
      },
      {
        path: '/content-library',
        name: 'content-library',
        component: ContentLibrary,
        beforeEnter: [
          hubspotTrackPageView,
          () => {
            if (store.getters['shop/hideMedia']) {
              return { name: 'overview' }
            }
          },
        ],
        meta: { title: PAGE_NAME_MAPPING.contentLibrary },
      },
      {
        path: '/segments',
        name: 'segments',
        component: Segments,
        beforeEnter: [
          hubspotTrackPageView,
          () => {
            if (!unleashClient.isEnabled(FLAG_SEGMENTS)) {
              return { name: 'overview' }
            }
          },
        ],
        meta: { title: PAGE_NAME_MAPPING.segments },
      },
      {
        path: '/segments/create',
        name: 'segment-create',
        component: () => import('@/views/SegmentCreation.vue'),
        beforeEnter: [
          hubspotTrackPageView,
          () => {
            if (!unleashClient.isEnabled(FLAG_SEGMENTS)) {
              return { name: 'overview' }
            }
          },
        ],
        meta: { title: PAGE_NAME_MAPPING.segmentCreate, back: { name: 'segments' } },
      },
      {
        path: '/segments/:id/edit',
        name: 'segment-edit',
        component: () => import('@/views/SegmentEditing.vue'),
        beforeEnter: [
          hubspotTrackPageView,
          () => {
            if (!unleashClient.isEnabled(FLAG_SEGMENTS)) {
              return { name: 'overview' }
            }
          },
        ],
        meta: { title: PAGE_NAME_MAPPING.segmentEdit, back: { name: 'segments' } },
      },
      {
        path: '/:pathMatch(.*)*',
        redirect: '/404',
      },
    ],
  },
  {
    path: '/',
    beforeEnter: [publicGuard],
    children: [
      {
        path: '/404',
        name: 'not-found',
        component: NotFound,
        meta: { title: PAGE_NAME_MAPPING.notFound },
      },
      {
        path: '/error',
        name: 'error',
        component: ErrorPage,
        meta: { title: PAGE_NAME_MAPPING.error },
      },
      {
        path: '/admin-failure',
        name: 'admin-failure',
        component: AdminError,
        meta: { title: PAGE_NAME_MAPPING.adminError },
      },
    ],
  },
  {
    path: '/logout',
    name: 'logout',
    component: LogoutPage,
    meta: { title: PAGE_NAME_MAPPING.logout },
  },
  {
    path: '/no-access',
    name: 'no-access',
    component: BannedUser,
    meta: { title: 'You have no access' },
  },
  {
    path: '/login',
    name: 'login',
    beforeEnter: [loginRedirect],
    component: defineComponent({ render: () => null }),
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    beforeEnter: [loginRedirect],
    component: defineComponent({ render: () => null }),
  },
  {
    path: '/register',
    name: 'register',
    beforeEnter: [registrationRedirect],
    component: defineComponent({ render: () => null }),
  },
  {
    path: '/register-advertiser',
    name: 'register-advertiser',
    beforeEnter: [registrationRedirect],
    component: defineComponent({ render: () => null }),
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/404',
  },
]
